import { palette } from "@brexhq/metal-foundation-shared";
import styled from "@emotion/styled";
import { useInView } from "framer-motion";
import React, { useRef } from "react";

import { ArticleByline } from "@brexhq/next/src/components/building-blocks/ArticleByline/ArticleByline";
import { ArticleLinkList } from "@brexhq/next/src/components/building-blocks/ArticleLinkList/ArticleLinkList";
import { ButtonRow } from "@brexhq/next/src/components/building-blocks/ButtonRow/ButtonRow";
import {
  Chip,
  mapSanityPropsToChipProps,
} from "@brexhq/next/src/components/building-blocks/Chip/Chip";
import { ComparisonTable } from "@brexhq/next/src/components/building-blocks/ComparisonTable/ComparisonTable";
import { CreditCardBlock } from "@brexhq/next/src/components/building-blocks/CreditCardBlock/CreditCardBlock";
import { DividerLine } from "@brexhq/next/src/components/building-blocks/DividerLine/DividerLine";
import { FeaturedArticle } from "@brexhq/next/src/components/building-blocks/FeaturedArticle/FeaturedArticle";
import { IFrame } from "@brexhq/next/src/components/building-blocks/IFrame/IFrame";
import { IconPicker } from "@brexhq/next/src/components/building-blocks/IconPicker/IconPicker";
import { InlineSignupForm } from "@brexhq/next/src/components/building-blocks/InlineSignupForm/InlineSignupForm";
import { LightboxVideoLink } from "@brexhq/next/src/components/building-blocks/LightboxVideoLink/LightboxVideoLink";
import { List } from "@brexhq/next/src/components/building-blocks/List/List";
import { MarketoForm } from "@brexhq/next/src/components/building-blocks/MarketoForm/MarketoForm";
import { MediaBlock } from "@brexhq/next/src/components/building-blocks/MediaBlock/MediaBlock";
import { TextRow } from "@brexhq/next/src/components/building-blocks/TextRow/TextRow";
import { HorizontalBlocks } from "@brexhq/next/src/components/modules/HorizontalBlocks/HorizontalBlocks";
import { LeadFormController } from "@brexhq/next/src/components/modules/LeadForm/LeadformController";
import { OverlayPopup } from "@brexhq/next/src/components/modules/OverlayPopup/OverlayPopup";
import { TextOverImage } from "@brexhq/next/src/components/modules/TextOverImage/TextOverImage";
import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import { extractTextContent } from "@brexhq/next/src/utils/extractTextContent";
import { Text } from "@brexhq/shared/components/Typography/Text";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";
import { SpacingValues } from "@brexhq/shared/types/SpacingScale";
import { BlockRowItems } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems";
import { ThemeTextComponents } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems/TextRow";
import { AlignmentOptions } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowsModule";

import { LeadFormBuildingBlock } from "../../modules/LeadForm/LeadFormBuildingBlock";

export const textAlignmentToAlignItems = {
  left: "flex-start",
  center: "center",
  right: "flex-end",
};

type BlockRowsProps = {
  fullHeightContent?: boolean;
  rows: BlockRowItems;
  theme: ModuleThemeType;
  alignment: AlignmentOptions;
  verticalAlignment?: "flex-start" | "center" | "flex-end";
  direction?: React.CSSProperties["flexDirection"];
  gap?: SpacingValues;
  maxWidth?: string | number;
  withBorder?: boolean;
  slideInUpViewThreshold?: number;
};

type BlockRowsWrapperProps = Omit<BlockRowsProps, "rows" | "theme">;

const BlockRowsWrapper = styled.span<BlockRowsWrapperProps>(
  ({
    theme,
    maxWidth,
    direction,
    alignment,
    verticalAlignment,
    gap,
    fullHeightContent,
  }) => {
    return {
      height: fullHeightContent ? "100%" : "auto",
      maxWidth,
      display: "flex",
      flexDirection: direction,
      textAlign: alignment,
      alignItems: textAlignmentToAlignItems[alignment],
      justifyContent: verticalAlignment,
      gap: theme.spacing.LRG[gap ?? "none"],
    };
  },
);

export const BlockRows: React.FC<BlockRowsProps> = ({
  maxWidth = "100%",
  rows,
  theme,
  alignment,
  verticalAlignment = "flex-start",
  direction = "column",
  gap = "none",
  withBorder = true,
  fullHeightContent = false,
  slideInUpViewThreshold = 0,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    amount: slideInUpViewThreshold / 100,
  });

  return (
    <BlockRowsWrapper
      fullHeightContent={fullHeightContent}
      ref={ref}
      maxWidth={maxWidth}
      direction={direction}
      alignment={alignment}
      verticalAlignment={verticalAlignment}
      gap={gap}
    >
      {rows.map((row, index) => {
        if (row._type === "textRow") {
          return (
            <TextRow
              index={index}
              textAnimation={row.textAnimation}
              maxWidth={row.maxWidth}
              fontFamily={row.fontFamily}
              key={row._key}
              theme={theme}
              pill={row.pill}
              marginTop={row.marginTop}
              minHeight={row.minHeight}
              textStyle={row.textStyle}
              fontWeightOverride={row.fontWeightOverride}
              textColor={row.textColor}
              blockContent={extractTextContent(row)}
              semanticTagOverride={row.semanticTagOverride}
              absolutePositioning={row?.absolutePositioning}
              absolutePositioningAdvancedOptions={
                row?.absolutePositioningAdvancedOptions
              }
              hangingPunctuation={row.hangingPunctuation}
              textAnimationSlideInUp={row.textAnimationSlideInUp}
              textColorOverride={row.textColorOverride}
              shouldAnimate={isInView}
              noWrap={row.noWrap}
            />
          );
        }

        if (row._type === "buttonRow") {
          return (
            <ButtonRow
              key={row._key}
              {...row}
              maxWidth={row.maxWidth}
              alignment={alignment}
              theme={theme}
            />
          );
        }
        if (row._type === "articleByline") {
          return (
            <ArticleByline
              key={row._key}
              showAuthor={row.showAuthor}
              showDate={row.showDate}
              showReadTime={row.showReadTime}
              theme={theme}
              showLink={row?.showLink}
              linkLabel={row?.linkLabel}
              url={row?.url}
            />
          );
        }

        if (row._type === "chip") {
          const props = mapSanityPropsToChipProps(row, theme);
          return <Chip key={row._key} {...props} />;
        }

        if (row._type === "lightboxVideoLink") {
          return (
            <LightboxVideoLink
              key={row._key}
              label={row.label}
              video={row.video}
              lightMode={row?.lightMode ?? false}
              videoMaxWidth={row?.videoMaxWidth}
            />
          );
        }

        if (row._type === "mediaBlock") {
          return (
            <MediaBlock
              key={row._key}
              marginTop={row.marginTop}
              desktopMedia={row.desktopMedia}
              mobileMedia={row.mobileMedia}
              imageStyle={row.imageStyle}
              verticalAnchorPoint={row.verticalAnchorPoint}
              horizontalAnchorPoint={row.horizontalAnchorPoint}
              setImageHeight={row.setImageHeight}
              desktopImageHeight={row.desktopImageHeight}
              mobileImageHeight={row.mobileImageHeight}
              setImageMaxWidth={row.setImageMaxWidth}
              desktopImageMaxWidth={row.desktopImageMaxWidth}
              mobileImageMaxWidth={row.mobileImageMaxWidth}
              link={row.link}
              animationTargets={row.animationTargets}
              programName={row.programName}
            />
          );
        }

        if (row._type === "blockInlineSSU") {
          const {
            standard: { textPrimary },
          } = getModuleThemeColors(theme);

          return (
            <InlineSignupForm
              id={row.anchor ?? ""}
              key={row._key}
              btnLabel={row.btnLabel}
              capture={row.capture}
              textColor={textPrimary}
            />
          );
        }

        if (row._type === "blockForm") {
          const FormHeaderComponent = row.formHeader ? (
            <Text
              as="h4"
              components={ThemeTextComponents["Subtitle M"]?.components}
              margin={{
                LRG: [0, 0, "16px", 0],
              }}
            >
              {row.formHeader}
            </Text>
          ) : null;

          return (
            <MarketoForm
              theme={theme}
              checkWorkEmail={row.checkWorkEmail}
              hideNonEmailFields={row.hideNonEmailFields}
              isInlineEmail={row.isInlineEmail}
              useArrowButtonOnMobile={row.useArrowButtonOnMobile}
              showSuccessMessageBackgroundColor={
                row.showSuccessMessageBackgroundColor
              }
              hideSuccessMessageImage={row.hideSuccessMessageImage}
              bypassSuccessContentUrl={row.bypassSuccessContentUrl}
              FormHeaderComponent={FormHeaderComponent}
              showBackground={row.showBackground}
              backgroundColor={row.backgroundColor}
              topSpacing={row.topSpacing}
              horizontalSpacing={row.horizontalSpacing}
              key={row._key}
              programName={row.programName}
              formId={row.formId}
              anchor={row.anchor}
              submitButtonLabel={row.buttonLabel}
              isChilipiperEnabled={row.isChilipiperEnabled}
              isQualifiedEnabled={row.isQualifiedEnabled}
              qualifiedExperienceId={row.qualifiedExperienceId}
              hiddenFields={row.hiddenFields}
            />
          );
        }

        if (row._type === "iconPicker") {
          return (
            <IconPicker
              key={row._key}
              theme={theme}
              size={row.size}
              color={row.color}
              marginTop={row.marginTop}
              icon={row.icon}
            />
          );
        }

        if (row._type === "dividerLine") {
          const {
            standard: { borders },
          } = getModuleThemeColors(theme);
          const colorOverride = row.colorOverride;
          const colorToUse =
            colorOverride === "white"
              ? palette.neutrals.white
              : colorOverride === "black"
              ? palette.neutrals.black
              : borders;

          return (
            <DividerLine
              key={row._key}
              color={colorToUse}
              style={row.style}
              marginTop={row.marginTop}
              isFullWidth={row.isFullWidth}
              thickness={row.thickness}
            />
          );
        }

        if (row._type === "list") {
          return <List theme={theme} key={row._key} {...row} />;
        }

        if (row._type === "leadForm") {
          return (
            <LeadFormController
              key={row._key}
              setShowFinalStateView={() => false}
              data={row}
              theme={theme}
            />
          );
        }
        if (row._type === "articleLinkList") {
          return <ArticleLinkList key={row._key} data={row} theme={theme} />;
        }
        if (row._type === "featuredArticle") {
          return <FeaturedArticle key={row._key} data={row} theme={theme} />;
        }
        if (row._type === "iFrame") {
          return <IFrame key={row._key} iFrameCode={row.iFrameCode} />;
        }
        if (row._type === "comparisonTable") {
          return (
            <ComparisonTable
              key={row._key}
              theme={theme}
              table={row}
              withBorder={withBorder}
            />
          );
        }
        if (row._type === "horizontalBlocks") {
          return <HorizontalBlocks key={row._key} theme={theme} data={row} />;
        }
        if (row._type === "overlayPopup") {
          return <OverlayPopup key={row._key} data={row} />;
        }
        if (row._type === "creditCardBlock") {
          return <CreditCardBlock key={row._key} data={row} theme={theme} />;
        }
        if (row._type === "textOverImageModule") {
          return (
            <TextOverImage
              fullHeightContent={fullHeightContent}
              key={row._key}
              data={row}
            />
          );
        }
        if (row._type === "signupForm") {
          return <LeadFormBuildingBlock key={row._key} data={row} />;
        }
        return null;
      })}
    </BlockRowsWrapper>
  );
};
